import React, {useState, useEffect}					from 'react';
import {useNavigate, useParams}						from 'react-router-dom';
import moment										from 'moment';

import Button										from '../ressources/components/Button';
import Badge										from '../ressources/components/Badge';
import Section										from '../ressources/components/Section';
import Avatar										from '../ressources/components/Avatar';
import Load											from '../ressources/components/Load/Load';
import Checkbox										from '../ressources/components/Checkbox';
import ButtonStickyContainer						from '../ressources/components/Button/ButtonStickyContainer';
import ExternalApiCard								from '../ressources/components/ExternalApiCard';
import Modal										from '../ressources/components/Modal';
import Input										from '../ressources/components/Input';
import Icon											from '../ressources/components/Icon';

import {useAppDispatch, useAppSelector}				from '../app/hooks';
import {postCompanyUsersApi}						from '../redux/companyUsers/companyUsers';
import {LanguageType}								from '../ressources/language/type';
import {User}										from '../redux/user/interface';
import {PostUserData}								from '../redux/companyUsers/interface';
import {InputValues}								from '../ressources/components/Input/interface';

import {ft_genNotification, ft_pushNotification} 	from '../ressources/functions/notification';
import {ft_formatUserName}							from '../ressources/functions/format';
import {ft_setVarText}								from '../ressources/functions/text';

import img_link										from '../ressources/images/icon/link.svg';
import img_arrowRight								from '../ressources/images/icon/arrow/right.svg';
import {regex_domainUrlOnly}						from '../ressources/data/regex';
import LANGUAGE										from '../ressources/language';
import * as API										from '../ressources/API';

import './Users.css';


interface Establishment {
	id: string
	name: string
	usersCount?: string
}

interface ImportData {
	user: User & {establishment?: Establishment}
	exist: boolean
	selected: boolean
}

function UserHRIS() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const users = useAppSelector(state => state.companyUsers.data);
	const usersLoad = useAppSelector(state => state.companyUsers.isLoad);
	const lang = useAppSelector(state => state.settings.lang);
	let notificationId = useAppSelector(state => state.notifications.lastId);

	const [view, set_view] = useState<'home' | keyof LanguageType['component']['externalApi']>('home');
	const [connected, set_connected] = useState<{name: string, establishments: string[]}[]>([]);

	const [modal, set_modal] = useState<boolean | null>(null);
	const [url, set_url] = useState<InputValues>('');
	const [token, set_token] = useState<InputValues>('');

	const [imported, set_imported] = useState<ImportData[]>([]);
	const [load, set_load] = useState(false);
	const [loadPost, set_loadPost] = useState(false);
	const [establishments, set_establishments] = useState<Establishment[]>([]);
	const [establishmentsSelect, set_establishmentsSelect] = useState<string[]>([]);
	const [step, set_step] = useState(0);

	const navigate = useNavigate();
	const params = useParams();
	const TEXTE = LANGUAGE(lang);

	useEffect(() => set_view(params.view as keyof LanguageType['component']['externalApi'] || 'home'), [params.view]);

	useEffect(getHris, []);

	useEffect(() => {
		if (!view || view === 'home' || usersLoad || connected.length === 0)
			return;

		const getUsers = () => {
			set_step(1);

			API.importUserHris(view, user)
			.then((response) => {
				if (!response || response.error) {
					set_load(false);

					if (view === 'lucca') {
						set_view('home');
						return set_modal(true);
					}
					return window.location.replace(`${import.meta.env.VITE_API}/users/auth/${view}/init/${user?._id}`);
				}

				const result: ImportData[] = [];

				response?.data?.forEach((data: User) => {
					if (!data?.email)
						return;

					let exist = false;

					if (users.some(find => find?.email.toLowerCase() === data.email.toLowerCase()))
						exist = true;
					if (users.some(find => ft_formatUserName(find) === ft_formatUserName(data)))
						exist = true;

					result.push({
						exist,
						user:     data,
						selected: !exist
					});
				});

				set_load(false);
				set_imported(result.sort(a => (a.exist ? 1 : -1)));
			});
		};
		set_load(true);

		if (connected.some(f => f.name === view && f.establishments?.length))
			set_establishmentsSelect(connected.find(f => f.name === view)?.establishments as string[]);

		if (view === 'lucca' && step === 0) {
			API.importUserHris('luccaEstablishments', user)
			.then((response) => {
				if (!response || response.error)
					return getUsers();

				set_establishments(response.data);

				if (connected.some(f => f.name === 'lucca' && f.establishments?.length) || response.data?.filter((f: Establishment) => f.usersCount).length <= 1)
					return getUsers();
			});
		} else
			getUsers();
	}, [view, usersLoad, connected]);

	useEffect(() => {
		if (modal)
			return;
		set_url('');
		set_token('');
	}, [modal]);

	function getHris(cb?: () => void) {
		API.getUserOauth(user, true)
		.then((response) => {
			if (response.data)
				set_connected(response.data);
			if (cb)
				return cb();
		});
	}

	function connectLucca() {
		set_load(true);
		API.postHirs('lucca', {url: url as string, token: token as string}, user)
		.then((response) => {
			set_load(false);

			if (!response.status) {
				const notificationData = ft_genNotification(TEXTE, 'mdmConnectError', img_link, undefined, 'error');
				return ft_pushNotification(dispatch, notificationId, notificationData);
			}

			const notificationData = ft_genNotification(TEXTE, 'mdmConnectSuccess', img_link, TEXTE.component.externalApi.lucca);
			ft_pushNotification(dispatch, notificationId, notificationData);

			set_modal(null);
			navigate('/users/hris/lucca');
		});
	}

	function setEstablishments() {
		API.putHirs(view, {establishments: establishmentsSelect}, user)
		.then((response) => {
			if (!response.status) {
				const notificationData = ft_genNotification(TEXTE, 'mdmConnectError', img_link, undefined, 'error');
				return ft_pushNotification(dispatch, notificationId, notificationData);
			}

			set_establishmentsSelect([]);
			set_step(1);
			getHris();
		});
	}

	async function addUsers() {
		let successCount = 0;
		set_loadPost(true);

		await Promise.all(imported.map(importData =>
			new Promise((resolve) => {
				if (!importData?.user || !importData.selected || importData.exist)
					return resolve(true);

				const apiData: PostUserData = {
					noHubspot:     true,
					job:           importData.user.job,
					email:         importData.user.email,
					phone:         importData.user.phone,
					lastname:      importData.user.lastname,
					firstname:     importData.user.firstname,
					addMethod:     view !== 'home' ? view : 'manually',
					contractEnd:   importData.user.contractEnd ? moment(importData.user.contractEnd).toDate() : undefined,
					contractStart: importData.user.contractStart ? moment(importData.user.contractStart).toDate() : undefined
				};

				dispatch(postCompanyUsersApi({data: apiData, user}))
				.then(async (response) => {
					if (!response.payload._id) {
						const notificationData = ft_genNotification(TEXTE, 'error', img_link, response.payload.error, 'error');
						notificationId = ft_pushNotification(dispatch, notificationId, notificationData);
					} else
						successCount++;

					resolve(true);
				});
			})
		));

		const notificationData = ft_genNotification(TEXTE, 'addMultipleUsersSuccess', img_link, successCount.toString());
		notificationId = ft_pushNotification(dispatch, notificationId, notificationData);

		set_loadPost(false);
		navigate('/users');
	}

	function renderImportButton(name: keyof LanguageType['component']['externalApi'], disabled?: boolean) {
		const isConnected = !disabled && connected.find(find => find.name === name);
		const ACTIONS_LIST = [];

		if (isConnected) {
			ACTIONS_LIST.push({
				value:  'import',
				render: <span className={'text-xs weight-600 gray-700'}>{TEXTE.users.hris.import}</span>
			});
		}

		return (
			<ExternalApiCard
				name={name}
				disabled={disabled}
				actionList={ACTIONS_LIST}
				isConnected={!!isConnected}
				text={TEXTE.users.hris.importText}
				title={TEXTE.users.hris.importUsers}
				onClickAction={(action) => {
					if (isConnected && action === 'import')
						return navigate(`/users/hris/${name}`);
					if (name === 'lucca')
						return set_modal(true);
					return window.open(`${import.meta.env.VITE_API}/users/auth/${name}/init/${user?._id}`);
				}}
			/>
		);
	}

	function renderEstablishment() {
		const render: React.ReactElement[] = [];

		establishments.forEach((establishment) => {
			const onClickEstablishment = () => {
				const copy = [...establishmentsSelect];

				if (index === -1)
					copy.push(`${establishment.id}`);
				else
					copy.splice(index, 1);

				set_establishmentsSelect(copy);
			};
			const index = establishmentsSelect.findIndex(f => f === `${establishment.id}`);

			render.push(
				<div
					onClick={onClickEstablishment}
					className={`globalDataContainerItem deviceMdmGroupListItem ${index !== -1 && 'active'}`}
				>
					<Checkbox active={index !== -1} />
					<span>{establishment.name}</span>
					<Badge
						size={'sm'}
						color={'success'}
						text={`${establishment.usersCount} ${TEXTE.settings.users}`}
					/>
				</div>
			);
		});

		return (
			<div>
				<span className={'text-lg weight-600 gray-900'}>{TEXTE.users.hris.establishments}</span>
				{render}
			</div>
		);
	}

	function renderImport() {
		if (view === 'lucca' && step === 0)
			return renderEstablishment();

		const onClickAll = () => {
			const copy = [...imported];

			copy.forEach((e) => {
				if (!e.exist)
					e.selected = !allSelected;
			});

			set_imported(copy);
		};
		const renderTable: React.ReactElement[] = [];
		const allSelected = !imported.some(find => !find.exist && !find.selected);

		imported.forEach((importData, i) => {
			const onClick = () => {
				if (importData.exist)
					return;

				const copy = [...imported];

				copy[i].selected = !copy[i].selected;

				set_imported(copy);
			};

			renderTable.push(
				<tr
					onClick={onClick}
					key={`import-${i}`}
					style={{cursor: importData.exist ? 'default' : 'pointer'}}
					className={importData.selected ? 'tableRow selected' : ''}
				>
					<td>
						<div className={'flex-center'}>
							{importData.exist ?
								<Badge text={TEXTE.global.valala} size={'sm'} />
								:
								<Checkbox active={importData.selected} />
							}
						</div>
					</td>
					<td>
						<div>
							<Avatar
								firstname={importData.user?.firstname}
								lastname={importData.user?.lastname}
							/>
							<span className={'text-xs weight-500 gray-900'}>{ft_formatUserName(importData.user)}</span>
						</div>
					</td>
					<td>{importData.user?.email}</td>
					{view === 'google' && <td>{importData.user?.phone}</td>}
					{view === 'lucca' && <td>{importData.user?.job}</td>}
					{view === 'lucca' && <td>{importData.user?.contractStart && moment(importData.user?.contractStart).format('DD/MM/YYYY')}</td>}
					{view === 'lucca' && <td>{importData.user?.contractEnd && moment(importData.user?.contractEnd).format('DD/MM/YYYY')}</td>}
					{view === 'lucca' && <td>{importData.user?.establishment?.name}</td>}
				</tr>
			);
		});

		if (load)
			return <Load center />;

		return (
			<table className={'importTable'}>
				<thead>
					<tr style={{textAlign: 'left'}}>
						<th style={{width: 50}}>
							<div className={'flex-center'}>
								<Checkbox onChange={onClickAll} active={allSelected} />
							</div>
						</th>
						<th>{TEXTE.global.name}</th>
						<th>{TEXTE.global.email}</th>
						{view === 'google' && <th>{TEXTE.global.phone}</th>}
						{view === 'lucca' && <th>{TEXTE.global.job}</th>}
						{view === 'lucca' && <th>{TEXTE.global.contractStart}</th>}
						{view === 'lucca' && <th>{TEXTE.global.contractEnd}</th>}
						{view === 'lucca' && <th>{TEXTE.users.hris.establishment}</th>}
					</tr>
				</thead>
				<tbody>
					{renderTable}
				</tbody>
			</table>
		);
	}

	function renderButtonContainer() {
		return (
			<ButtonStickyContainer>
				<Button
					fullWidth
					disabled={loadPost}
					type={'tertiary-gray'}
					onClick={() => navigate('/users/hris')}
				>
					{TEXTE.global.cancel}
				</Button>

				{view === 'lucca' && step === 0 ?
					<Button
						fullWidth
						isLoad={loadPost}
						onClick={setEstablishments}
						disabled={establishmentsSelect.length === 0}
					>
						{TEXTE.global.next}
					</Button>
					:
					<Button
						fullWidth
						isLoad={loadPost}
						onClick={addUsers}
						disabled={!imported.some(find => find.selected)}
					>
						{TEXTE.global.confirm}
					</Button>
				}
			</ButtonStickyContainer>
		);
	}

	function renderModal() {
		return (
			<Modal
				size={503}
				confirmLoad={load}
				confirmButton={connectLucca}
				onClose={() => set_modal(null)}
				footerFlexDirection={'column-reverse'}
				confirmButtonDisabled={!regex_domainUrlOnly.test(url as string) || !token}
				header={<div className={'externalApiCardLogo lucca flex flex-center'} />}
				confirmButtonText={ft_setVarText(TEXTE.global.connectTo, TEXTE.component.externalApi.lucca)}
			>
				<span className={'display-xs weight-700 gray-900 text-center'}>{ft_setVarText(TEXTE.users.hris.connectTo, TEXTE.component.externalApi.lucca)}</span>
				<span className={'text-md gray-500 text-center'} style={{marginTop: 16}}>
					{ft_setVarText(TEXTE.users.hris.connexionWithvalala, TEXTE.component.externalApi.lucca)}
				</span>

				<div className={'globalFormContainer'} style={{marginTop: 32}}>
					<Input
						required
						value={url}
						onChange={set_url}
						title={TEXTE.users.hris.url}
						placeholder={'https://valala.ilucca.net'}
						error={url && !regex_domainUrlOnly.test(url as string)}
					/>
					<Input
						required
						value={token}
						onChange={set_token}
						title={TEXTE.users.hris.apiKey}
						placeholder={'XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX'}
					/>
				</div>
			</Modal>
		);
	}

	return (
		<div className={'globalDataContainer'}>
			{view === 'home' ?
				<div className={'globalDataContainerItem column'}>
					{renderImportButton('google')}
					{renderImportButton('lucca')}
					{renderImportButton('payfit')}
					{renderImportButton('silae', true)}
				</div>
				:
				<Section className={'globalDataContainerItem'}>
					<span className={'text-lg weight-600 gray-900'}>{ft_setVarText(TEXTE.users.hris.importUsers, TEXTE.component.externalApi[view])}</span>
				</Section>
			}

			{(view === 'lucca' && step === 1 && establishments.length > 1) &&
				<div
					className={'flex-row'}
					onClick={() => set_step(0)}
					style={{cursor: 'pointer', gap: 8, alignItems: 'center', marginBottom: 16}}
				>
					<Icon
						iconSize={16}
						iconColor={'#000'}
						icon={img_arrowRight}
						style={{transform: 'rotate(180deg)', width: 24, height: 24}}
					/>
					<span className={'weight-600 gray-900'}>{TEXTE.users.hris.changeEstablishment}</span>
				</div>
			}
			{view === 'google' && renderImport()}
			{view === 'lucca' && renderImport()}
			{view === 'payfit' && renderImport()}

			{view !== 'home' && renderButtonContainer()}

			{modal && renderModal()}
		</div>
	);
}

export default UserHRIS;
